// シンプルなレジストリ定義

type Category = 'ui' | 'example';

type RegistryFileInput =
  | string
  | {
      readonly path: string;
      readonly type?: 'registry:component' | 'registry:file';
      readonly target?: string;
    };

interface RegistryItemOptions {
  readonly description: string;
  readonly files: RegistryFileInput[];
  readonly category: Category;
  readonly dependencies?: string[];
}

function normalizeFile(file: RegistryFileInput) {
  if (typeof file === 'string') {
    return {
      path: file,
      type: 'registry:component' as const,
    };
  }

  return {
    path: file.path,
    type: (file.type ?? 'registry:component') as 'registry:component' | 'registry:file',
    ...(file.target ? { target: file.target } : {}),
  };
}

function createItem(name: string, options: RegistryItemOptions) {
  return {
    name,
    type: 'registry:component' as const,
    description: options.description,
    files: options.files.map((file) => normalizeFile(file)),
    category: options.category,
    ...(options.dependencies && options.dependencies.length > 0
      ? { registryDependencies: options.dependencies }
      : {}),
  };
}

function withPrefix(files: RegistryFileInput[], prefix: 'ui' | 'example') {
  return files.map((file) => {
    if (typeof file === 'string') {
      return `${prefix}/${file}`;
    }

    return {
      ...file,
      path: `${prefix}/${file.path}`,
    } as RegistryFileInput;
  });
}

function uiItem(
  name: string,
  description: string,
  files: RegistryFileInput[],
  dependencies?: string[],
) {
  return createItem(name, {
    description,
    files: withPrefix(files, 'ui'),
    category: 'ui',
    dependencies,
  });
}

function exampleItem(
  slug: string,
  description: string,
  files: RegistryFileInput[],
  dependencies?: string[],
) {
  return createItem(slug, {
    description,
    files: withPrefix(files, 'example'),
    category: 'example',
    dependencies,
  });
}

export const registry = [
  uiItem('accordion', 'Accordions for collapsible content sections', ['Accordion.astro']),
  uiItem('background-effect', 'Visual background effect surface with StylePack tokens', [
    'BackgroundEffect.astro',
  ]),
  uiItem('brand-logo', 'Brand logo row/grid primitive', [
    'BrandLogo.astro',
    {
      path: 'assets/pablo-logo-min.svg',
      type: 'registry:file',
      target: 'src/assets/ui/pablo-logo-min.svg',
    },
  ]),
  uiItem('button', 'Versatile button component with variants', ['Button.astro']),
  uiItem('card', 'Container card with size variants', ['Card.astro']),
  uiItem(
    'carousel',
    'Carousel container with controls and slots',
    ['Carousel.astro', 'CarouselItem.astro'],
    ['carousel-item'],
  ),
  uiItem('carousel-item', 'Carousel item with slot for content', ['CarouselItem.astro']),
  uiItem('grid-cluster', 'Grid layout cluster for showcasing items', ['GridCluster.astro']),
  uiItem('scroll-rail', 'Scroll progress rail indicator', ['ScrollRail.astro']),
  uiItem(
    'scroll-sidebar',
    'Sidebar layout with synced navigation and content',
    [
      'ScrollSidebar.astro',
      'ScrollSidebarContent.astro',
      'ScrollSidebarNav.astro',
      'ScrollSidebarNavItem.astro',
      'ScrollSidebarSection.astro',
    ],
    [
      'scroll-sidebar-content',
      'scroll-sidebar-nav',
      'scroll-sidebar-nav-item',
      'scroll-sidebar-section',
    ],
  ),
  uiItem('scroll-sidebar-content', 'Content container for Scroll Sidebar layout', [
    'ScrollSidebarContent.astro',
  ]),
  uiItem('scroll-sidebar-nav', 'Navigation wrapper for Scroll Sidebar', ['ScrollSidebarNav.astro']),
  uiItem('scroll-sidebar-nav-item', 'Navigation item for Scroll Sidebar sections', [
    'ScrollSidebarNavItem.astro',
  ]),
  uiItem('scroll-sidebar-section', 'Content section for Scroll Sidebar layout', [
    'ScrollSidebarSection.astro',
  ]),
  uiItem(
    'tabs',
    'Tabs primitive composed of list, trigger and content components',
    ['Tabs.astro', 'TabsList.astro', 'TabsContent.astro', 'TabsTrigger.astro'],
    ['tabs-list', 'tabs-content', 'tabs-trigger'],
  ),
  uiItem('tabs-content', 'Content panel for tabbed interfaces', ['TabsContent.astro']),
  uiItem('tabs-list', 'List container for tab triggers', ['TabsList.astro']),
  uiItem('tabs-trigger', 'Trigger button for selecting a tab', ['TabsTrigger.astro']),
  uiItem('webgl-rgb', 'WebGL RGB displacement effect', ['WebglRgb.astro']),
  exampleItem('cta/centered', 'Centered CTA section with badge and card grid', [
    'CTA/SectionCta_Centered.astro',
  ]),
  exampleItem(
    'faq/accordion',
    'FAQ section combining Accordion component',
    ['FAQ/SectionFaq_Accordion.astro'],
    ['accordion'],
  ),
  exampleItem(
    'faq/sidebar-layout',
    'FAQ section with sidebar navigation layout',
    ['FAQ/SectionFaq_SidebarLayout.astro'],
    ['accordion', 'scroll-sidebar'],
  ),
  exampleItem(
    'feature/bento-grid',
    'Feature grid using Card components and imagery',
    ['Feature/SectionFeature_BentoGrid.astro'],
    ['card'],
  ),
  exampleItem(
    'feature/carousel',
    'Feature carousel with Tabs-driven content',
    ['Feature/SectionFeature_Carousel.astro'],
    ['tabs', 'tabs-list', 'tabs-trigger', 'tabs-content', 'carousel'],
  ),
  exampleItem(
    'feature/grid-tabs',
    'Feature section with grid-based tabs',
    ['Feature/SectionFeature_GridTabs.astro'],
    ['tabs', 'tabs-list', 'tabs-trigger', 'tabs-content'],
  ),
  exampleItem(
    'feature/horizontal-tabs',
    'Horizontal tab layout for feature comparison',
    ['Feature/SectionFeature_HorizontalTabs.astro'],
    ['tabs', 'tabs-list', 'tabs-trigger', 'tabs-content'],
  ),
  exampleItem(
    'feature/offset-cards',
    'Offset card layout showcasing features',
    ['Feature/SectionFeature_OffsetCards.astro'],
    ['card'],
  ),
  exampleItem(
    'feature/scroll-sidebar',
    'Scroll synced sidebar feature index',
    ['Feature/SectionFeature_ScrollSidebar.astro'],
    ['scroll-sidebar'],
  ),
  exampleItem('feature/split-layout', 'Split layout with media and content alignment', [
    'Feature/SectionFeature_SplitLayout.astro',
  ]),
  exampleItem(
    'feature/tiles',
    'Tile-based feature gallery with cards',
    ['Feature/SectionFeature_Tiles.astro'],
    ['card'],
  ),
  exampleItem('footer/multi-column', 'Multi-column footer with navigation groups', [
    'Footer/Footer_MultiColumn.astro',
  ]),
  exampleItem('footer/simple-centered', 'Simple centered footer layout', [
    'Footer/Footer_SimpleCentered.astro',
  ]),
  exampleItem('header/floating-minimal', 'Floating minimal header with navigation', [
    'Header/Header_FloatingMinimal.astro',
  ]),
  exampleItem(
    'hero/centered-with-logo-cloud',
    'Centered hero with supporting logo cloud',
    ['Hero/SectionHero_CenteredWithLogoCloud.astro'],
    ['brand-logo'],
  ),
  exampleItem('hero/left-aligned', 'Left aligned hero with primary CTA', [
    'Hero/SectionHero_LeftAligned.astro',
  ]),
  exampleItem('hero/side', 'Side-by-side hero layout with imagery', [
    'Hero/SectionHero_Side.astro',
  ]),
  exampleItem(
    'how-to/vertical-tabs',
    'Step-by-step how-to section using vertical tabs',
    ['HowTo/SectionHowTo_VerticalTabs.astro'],
    ['tabs', 'tabs-list', 'tabs-trigger', 'tabs-content'],
  ),
  exampleItem(
    'logo/cloud-inline',
    'Inline logo cloud strip',
    ['Logo/LogoCloudInline.astro'],
    ['brand-logo'],
  ),
  exampleItem(
    'logo/cloud-simple',
    'Simple stacked logo cloud',
    ['Logo/LogoCloudSimple.astro'],
    ['brand-logo'],
  ),
  exampleItem(
    'pricing/three-column',
    'Pricing table with highlighted middle tier',
    ['Pricing/SectionPricing_ThreeColumn.astro'],
    ['card'],
  ),
  exampleItem(
    'stats/centered',
    'Centered statistics grid with framing card',
    ['Stats/SectionStats_Centered.astro'],
    ['card'],
  ),
  exampleItem('stats/grid', 'Large stats grid with supporting copy', [
    'Stats/SectionStats_Grid.astro',
  ]),
  exampleItem(
    'testimonials/auto-scroll',
    'Auto scrolling testimonial rail',
    ['Testimonials/SectionTestimonials_AutoScroll.astro'],
    ['scroll-rail'],
  ),
  exampleItem(
    'testimonials/carousel',
    'Testimonial carousel with media focus',
    ['Testimonials/SectionTestimonials_Carousel.astro'],
    ['carousel'],
  ),
  exampleItem(
    'testimonials/carousel-features',
    'Feature-focused testimonial carousel',
    ['Testimonials/SectionTestimonials_CarouselFeatures.astro'],
    ['carousel'],
  ),
  exampleItem('testimonials/masonry-grid', 'Masonry grid testimonial layout', [
    'Testimonials/SectionTestimonials_MasonryGrid.astro',
  ]),
];
